import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomDropdown from '@components/CustomDropdown';
import { toastError, toastSuccess } from '@components/Toast';
// import CommonImage from '@components/CommonImage';

import FitType from '@features/blockSuggestion/components/FitType';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import { getLoadingState, isValidLink, printApiError } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

import {
	useCreateArtboardProductTagMutation,
	useDeleteArtboardProductTagMutation,
	useGetArtboardProductTagQuery,
	useUpdateArtboardProductTagMutation
} from '@/features/blockSuggestion/api/blockSuggestionApiSlice';
import CommonImage from '@/components/CommonImage';
import LoaderComponent from '@/components/LoaderComponent';
import useMultipartFileUpload from '@/hooks/useMultipartFileUpload';

const FileUploads = ({
	id,
	originFrom = 'FILE',
	onSetActiveTool,
	onPlaceImageOnArtboard,
	entityType = 'PRODUCT',
	allowedFileType = '*',
	isShowArtworkId = false
}) => {
	const [isLinkClicked, setIsLinkClicked] = useState(false);
	const [fileTypes, setFileTypes] = useState([]);
	const [selectedFileType, setSelectedFileType] = useState(null);
	const [fileList, setFileList] = useState({});
	const [link, setLink] = useState('');
	const [inValidLink, setInValidLink] = useState(false);
	const [isError, setIsError] = useState(false);
	const [isLinkError, setIsLinkError] = useState(false);
	const dispatch = useDispatch();
	const [isShowHistory, setIsShowHistory] = useState(false);
	const toolTipRef = useRef();
	const [selectedArtboardIdToEdit, setSelectedArtboardIdToEdit] = useState(null);
	const [artboardReferenceNumberInput, setArtboardReferenceNumberInput] = useState('');

	const { data: artworkIds, refetch: refetchArtboardIds } = useGetArtboardProductTagQuery({
		productId: id
	});
	const [deleteArtboardProductTag, { isLoading: isDeleteArtboardProductTagLoading }] =
		useDeleteArtboardProductTagMutation();
	const [createArtboardProductTag, { isLoading: isCreateArtboardProductTagLoading }] =
		useCreateArtboardProductTagMutation();
	const [updateArtboardProductTag, { isLoading: isUpdateArtboardProductTagLoading }] =
		useUpdateArtboardProductTagMutation();

	const { uploadFiles, isUploading: isMultipartUploading } = useMultipartFileUpload({
		onSuccess: () => {
			setIsError(false);
			fetchProductFile();
			setIsLinkClicked(false);
			setInValidLink(false);
			setSelectedFileType(null);
		},
		onError: () => {
			setIsError(true);
		},
		showToasts: true
	});

	useEffect(() => {
		if (id) {
			fetchProductFile();
			(async () => {
				const { data } = await ApiClient.GET(
					`${BASE_URL}/entity-file/file-type/${entityType}`,
					'',
					true
				);
				let tmpData = data?.map((item) => ({ id: item.constant, label: item.name }));
				setFileTypes(tmpData);
			})();
		}
	}, [id]);

	const fetchProductFile = async () => {
		dispatch(openLoader());
		try {
			const { data } = await ApiClient.GET(
				`${BASE_URL}/entity-file/${id}/entityType/${entityType}`,
				'',
				true
			);
			setFileList(data);
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const onUpdateData = async (item, type, value) => {
		try {
			dispatch(openLoader());
			let body = {};
			if (type === 'remarks') body.remarks = value;
			else body.isFinal = true;
			await ApiClient.PUT('entityFile', body, `/${item.id}`);
			fetchProductFile();
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const onRemove = async (item) => {
		try {
			await ApiClient.DELETE('entityFile', {}, `/${item.id}`);
			await fetchProductFile();
		} catch (error) {
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const onLinkSave = () => {
		if (isValidLink(link)) {
			const requestBody = {
				isLink: true,
				link: link,
				fileType: selectedFileType.id,
				entityId: id,
				entityType: entityType
			};

			onFileUpload(requestBody);
		} else {
			setInValidLink(true);
		}
	};

	const onUploadFile = async (e, newlyUploadedFileType = null) => {
		const files = Array.from(e.target.files);
		if (!files.length) return;
		const documentType = getDocumentType(newlyUploadedFileType || selectedFileType.id);
		try {
			const results = await uploadFiles(files, documentType);

			if (results && results.length > 0) {
				await processUploadedFiles(results, newlyUploadedFileType || selectedFileType.id);
			}
		} catch (error) {
			printApiError(error);
		}
	};

	const getDocumentType = (fileType) => {
		switch (fileType) {
			case 'IMAGE':
				return 'PRODUCT_IMAGE';
			case 'DOCUMENT':
				return 'PRODUCT_DOCUMENT';
			default:
				return 'PRODUCT_NOT_IMAGE';
		}
	};

	const processUploadedFiles = async (uploadResults, fileType) => {
		try {
			dispatch(openLoader());
			const documentIdList = uploadResults.map((result) => result?.documentId).filter(Boolean);

			if (documentIdList.length > 0) {
				await ApiClient.POST('uploadEntityFile', {
					fileType: fileType,
					entityId: id,
					entityType: entityType,
					isLink: false,
					documentIdList: documentIdList
				});
				fetchProductFile();
			}
		} catch (error) {
			toastError(error?.data?.message || 'Failed to create file records in database');
		} finally {
			dispatch(closeLoader());
		}
	};

	const onFileUpload = async (fileData) => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.POST('uploadEntityFile', fileData);
			toastSuccess(data.message || 'File uploaded successfully.');
			fetchProductFile();
			setIsLinkClicked(false);
			setInValidLink(false);
			setSelectedFileType(null);
		} catch (error) {
			dispatch(closeLoader());
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		} finally {
			dispatch(closeLoader());
		}
	};

	const sortAndFilterFiles = (files) => {
		const filesData = files
			.filter((file) => {
				if (!isShowHistory) {
					return !file?.deletedAt;
				} else {
					return file?.deletedAt;
				}
			})
			.sort((a, b) => {
				if (a.isFinal && !b.isFinal) {
					return -1;
				}
				if (!a.isFinal && b.isFinal) {
					return 1;
				}
				return 0;
			});
		return filesData;
	};

	const handleCreateArtboardId = () => {
		if (artboardReferenceNumberInput) {
			createArtboardProductTag({
				params: {
					artboardReferenceNumber: artboardReferenceNumberInput,
					productId: id
				}
			})
				.unwrap()
				.then(() => {
					refetchArtboardIds();
					setArtboardReferenceNumberInput('');
				})
				.catch(({ error }) => {
					toastError(error?.data?.message || 'Something went wrong! Please try again.');
				});
		}
	};

	const handleUpdateArtboardId = () => {
		if (selectedArtboardIdToEdit?.artboardReferenceNumber) {
			updateArtboardProductTag({
				artworkArtboardId: selectedArtboardIdToEdit?.id,
				params: {
					artboardReferenceNumber: selectedArtboardIdToEdit?.artboardReferenceNumber,
					productId: id
				}
			})
				.unwrap()
				.then(() => {
					setSelectedArtboardIdToEdit(null);
					refetchArtboardIds();
				})
				.catch(({ error }) => {
					toastError(error?.data?.message || 'Something went wrong! Please try again.');
				});
		} else {
			setSelectedArtboardIdToEdit(null);
		}
	};

	const handleDeleteArtboardId = (item) => {
		deleteArtboardProductTag({
			artworkArtboardId: item?.id,
			params: {
				productId: id
			}
		})
			.unwrap()
			.then(() => {
				refetchArtboardIds();
			})
			.catch(({ error }) => {
				toastError(error?.data?.message || 'Something went wrong! Please try again.');
			});
	};

	const isLoading = getLoadingState([
		isDeleteArtboardProductTagLoading,
		isCreateArtboardProductTagLoading,
		isUpdateArtboardProductTagLoading,
		isMultipartUploading
	]);

	const renderFitTypes = (files) => {
		return (
			files?.length > 0 &&
			sortAndFilterFiles(files).map((item, index) => (
				<div className="all-uploaded-files mt-12" key={item.id}>
					<FitType
						item={item}
						onUpdateData={(type, value) => onUpdateData(item, type, value)}
						onImageUpload={(e) => {
							onUploadFile(e, item?.fileType?.constant);
						}}
						onRemove={() => onRemove(item, files[0])}
						originFrom={originFrom}
						onSetActiveTool={onSetActiveTool}
						index={index + 1}
						onPlaceImageOnArtboard={onPlaceImageOnArtboard}
						entityType={entityType}
					/>
				</div>
			))
		);
	};

	const handleOutsideClick = (e) => {
		if ((isError || isLinkError) && !toolTipRef?.current?.contains(e.target)) {
			setIsError(false);
			setIsLinkError(false);
		}
	};

	useEffect(() => {
		document.addEventListener('click', handleOutsideClick);
		return () => {
			document.removeEventListener('click', handleOutsideClick);
		};
	}, [isError, isLinkError]);

	const renderFileUpload = () => {
		return (
			<LoaderComponent loading={isLoading}>
				<div className="upload-and-link">
					{originFrom === 'FILE' && (
						<>
							<div className="file-type-dropdown-and-upload">
								<div className="file-type-dropdown">
									<CustomDropdown
										type="addItem"
										title={'File type'}
										isShowLabel={false}
										onItemClick={(value) => {
											setSelectedFileType(value);
											setIsError(false);
											setIsLinkError(false);
										}}
										items={fileTypes?.map((item) => ({ id: item.id, value: item.label }))}
										selectedItem={selectedFileType?.value}
									/>
								</div>
								<div
									className="category-wise-upload d-flex align-items-center mt-2"
									ref={toolTipRef}
								>
									<div className="text-view-uploader upload-template w-100">
										<label
											htmlFor="templateUpload"
											className="drag-upload cursor-pointer"
											onClick={() => {
												if (!selectedFileType?.value) {
													setIsError(true);
													setIsLinkError(false);
												}
											}}
										>
											<span className="custom-tooltip">
												<p className="regular-14 gray_dark_01">Upload </p>
												{isError && (
													<span className="tooltiptext bg-danger">
														First select file type
													</span>
												)}
											</span>
										</label>
										<input
											type="file"
											className="file-upload d-none"
											name="templateUpload"
											id="templateUpload"
											accept={allowedFileType}
											onClick={(e) => (e.target.value = null)}
											onChange={(e) => onUploadFile(e)}
											disabled={!selectedFileType?.value}
											multiple
										/>
									</div>
									<p className="regular-12 gray_dark_03 pr-6 pl-6">or</p>
									<button
										className="link-btn regular-12 gray_dark_02 cursor-pointer"
										onClick={() => {
											if (!selectedFileType?.value) {
												setIsLinkError(true);
												setIsError(false);
											} else setIsLinkClicked(true);
										}}
									>
										<span className="custom-tooltip">
											<p className="regular-14 gray_dark_01">Link</p>
											{isLinkError && (
												<span className="tooltiptext bg-danger">
													First select file type
												</span>
											)}
										</span>
									</button>
								</div>
							</div>
							{isLinkClicked && (
								<div className="link-paste d-flex align-items-center mt-2">
									<input
										type="text"
										autoComplete="off"
										placeholder="Link paste here"
										onChange={(e) => setLink(e.target.value)}
									/>
									<button className="close-url" onClick={() => setIsLinkClicked(false)}>
										<CommonImage src={`/icons/close.svg`} alt />
									</button>
									<button className="button size32 save" onClick={onLinkSave}>
										<CommonImage src={`/icons/tick.png`} alt />
									</button>
								</div>
							)}
							{inValidLink && <span className="regular-12 red">Invalid link</span>}
						</>
					)}
					{isShowArtworkId && (
						<div className="artboard-id-form mt-2">
							<div>
								<p className="regular-12 gray_dark_02 weight-500 mb-2">Artboard ID</p>
								<div className="from-and-option">
									<div className="artboard-id-form-input ">
										<div className="form-group">
											<input
												type="text"
												value={artboardReferenceNumberInput}
												onChange={(e) =>
													setArtboardReferenceNumberInput(e.target.value)
												}
												onBlur={() => {
													handleCreateArtboardId();
												}}
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														handleCreateArtboardId();
													}
												}}
											/>
										</div>
									</div>
								</div>
								{artworkIds?.map((item) => (
									<div className="from-and-option d-flex align-items-start justify-content-between">
										<div className="artboard-id-form-input w-100">
											<div className="form-group position-relative">
												{selectedArtboardIdToEdit?.id === item?.id ? (
													<input
														type="text"
														value={
															selectedArtboardIdToEdit?.artboardReferenceNumber
														}
														onChange={(e) =>
															setSelectedArtboardIdToEdit({
																...selectedArtboardIdToEdit,
																artboardReferenceNumber: e.target.value
															})
														}
														onBlur={handleUpdateArtboardId}
														onKeyDown={(e) => {
															if (e.key === 'Enter') {
																handleUpdateArtboardId();
															}
														}}
													/>
												) : (
													<div
														className="d-flex justify-content-between align-items-center"
														key={item?.id}
													>
														<div className="w-100">
															<p className="mb-0 gray_dark_01 regular-14 border p-2 w-100 d-flex justify-content-between align-items-center">
																{item?.artboardReferenceNumber}
																{item?.status === 'SOLD' && (
																	<span className="status status-sold red-3">
																		{item?.status}
																	</span>
																)}
															</p>
														</div>
													</div>
												)}
											</div>
										</div>

										<div className="option">
											<div className="dropdown">
												<button
													className="btn btn-default dropdown-toggle pr-0 pl-1 pt-0"
													type="button"
													id="menu1"
													data-toggle="dropdown"
													aria-expanded="false"
												>
													<CommonImage
														src="/icons/more_vertical.svg"
														alt=""
														width={16}
														height={16}
														className="mr-1"
													/>
												</button>
												<ul
													className="dropdown-menu dropdown-menu-right shadow-lg"
													role="menu"
													aria-labelledby="menu1"
													x-placement="bottom-end"
												>
													<li
														role="presentation"
														className="px-4 pb-3 pt-3"
														onClick={() => {
															setSelectedArtboardIdToEdit(item);
														}}
													>
														<span
															role="menuitem"
															tabIndex={-1}
															className="font-weight-normal  text-black"
														>
															Edit
														</span>
													</li>

													<li
														role="presentation"
														className="px-4 pb-3"
														onClick={() => handleDeleteArtboardId(item)}
													>
														<span
															role="menuitem"
															tabIndex={-1}
															className="font-weight-normal  text-black"
														>
															Delete
														</span>
													</li>
												</ul>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					<div className="all-files-and-history position-relative mt-3 pt-1">
						{!isShowHistory ? (
							<p
								className="semibold-12 gray_dark_01 text-underline cursor-pointer history-button"
								onClick={() => setIsShowHistory(true)}
							>
								History
							</p>
						) : (
							<p
								className="regular-12 weight-700 text-uppercase d-flex align-items-center cursor-pointer mb-3"
								onClick={() => setIsShowHistory(false)}
							>
								<CommonImage
									src={`/icons/back-arrow-gray.svg`}
									alt="back"
									className="mr-1"
									width={16}
									height={16}
								/>
								History
							</p>
						)}
						{Object.keys(fileList).length > 0 ? (
							Object.entries(fileList).map(([category, files]) => (
								<div key={category}>
									<h3 className="type-title regular-12 weight-700 gray_dark_02 mb-0">
										{sortAndFilterFiles(files).length > 0 && category.toUpperCase()}
									</h3>
									<div className="all-files">{renderFitTypes(files)}</div>
								</div>
							))
						) : (
							<div className="d-flex pt-5 mt-5 gray_dark_03 justify-content-center align-content-center">
								<span className="regular-14  gray-1">
									{isShowHistory ? 'No history found' : 'No file uploaded'}
								</span>
							</div>
						)}
					</div>
				</div>
			</LoaderComponent>
		);
	};

	return <div>{renderFileUpload()}</div>;
};

export default FileUploads;
